import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useToast } from '../../../common/hooks/useToast';
import { CrmPageFrame } from '../CrmPageFrame';
import { emailCampaignPath, newEmailCampaignPath } from '../crmPaths';
import { count } from '../manage/manage';
import { DeleteDialog, EmptyState, LoadingRows, StatTile, StatTiles, TABLE_SX, visuallyHidden } from '../manage/ManageParts';
import { useDebounced } from '../tagging/useDebounced';
import { CRM_DELETE_EMAIL_CAMPAIGN, CRM_DUPLICATE_EMAIL_CAMPAIGN, CRM_EMAIL_CAMPAIGNS, CRM_EMAIL_TOTALS } from './email.queries';
import { Campaign, describeEmailError, formatRate, isDeletable, isMigrated, STATUS_META } from './emailModel';
import { TestEmailDialog } from './SendDialogs';

type View = 'all' | 'drafts' | 'upcoming' | 'sent' | 'migrated';

const VIEWS: Record<View, { status: Array<string> | null; source: string | null }> = {
  all: { status: null, source: 'MANUAL' },
  drafts: { status: ['DRAFT'], source: 'MANUAL' },
  upcoming: { status: ['SCHEDULED', 'SENDING'], source: 'MANUAL' },
  sent: { status: ['SENT'], source: 'MANUAL' },
  migrated: { status: null, source: 'NATIONBUILDER' },
};

const TILES: Array<{ view: Exclude<View, 'all'>; label: string; hint: string; icon: React.ReactNode; tone: string }> = [
  { view: 'drafts', label: 'Drafts', hint: 'Being written', icon: <EditNoteOutlinedIcon />, tone: '#64748b' },
  { view: 'upcoming', label: 'Scheduled and sending', hint: 'On their way', icon: <ScheduleSendOutlinedIcon />, tone: '#4f46e5' },
  { view: 'sent', label: 'Sent', hint: 'From this dashboard', icon: <MarkEmailReadOutlinedIcon />, tone: '#059669' },
  { view: 'migrated', label: 'From NationBuilder', hint: 'History, with its figures', icon: <HistoryOutlinedIcon />, tone: '#7c3aed' },
];

const PAGE = 25;
const parseView = (value: string | null): View => (value && value in VIEWS ? (value as View) : 'all');

/** When it went, or when it will. */
const whenOf = (c: Campaign) => {
  if (c.status === 'SCHEDULED' && c.scheduledAt) return { label: `Goes ${dayjs(c.scheduledAt).format('D MMM, h:mm a')}`, at: c.scheduledAt };
  if (c.sentAt) return { label: dayjs(c.sentAt).format('D MMM YYYY'), at: c.sentAt };
  if (c.startedAt) return { label: `Started ${dayjs(c.startedAt).format('D MMM')}`, at: c.startedAt };
  return { label: `Edited ${dayjs(c.updatedAt).format('D MMM YYYY')}`, at: c.updatedAt };
};

/** Email campaigns: sent, scheduled and drafts, with their figures, and NationBuilder's as history. Plan 4.4. */
export const CrmEmailCampaigns = () => {
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get('show'));
  const search = params.get('q') ?? '';
  const page = Math.max(0, Number(params.get('page')) || 0);
  const go = (next: { view?: View; search?: string; page?: number }) => {
    const p = new URLSearchParams();
    const v = next.view ?? view;
    const s = next.search ?? search;
    if (v !== 'all') p.set('show', v);
    if (s.trim()) p.set('q', s.trim());
    if (next.page) p.set('page', String(next.page));
    setParams(p, { replace: true });
  };

  const [typed, setTyped] = useState(search);
  const settled = useDebounced(typed.slice(0, 200));
  useEffect(() => {
    if (settled.trim() !== search.trim()) go({ search: settled, page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const [testing, setTesting] = useState(false);

  const { data, previousData, loading, error, refetch } = useQuery<{ crmEmailCampaigns: { total: number; items: Array<Campaign> } }>(CRM_EMAIL_CAMPAIGNS, {
    variables: { ...VIEWS[view], search: search.trim() || null, skip: page * PAGE, take: PAGE },
    notifyOnNetworkStatusChange: true,
  });
  const totals = useQuery<Record<string, { total: number }>>(CRM_EMAIL_TOTALS);
  const [duplicate] = useMutation(CRM_DUPLICATE_EMAIL_CAMPAIGN);
  const [remove, removal] = useMutation(CRM_DELETE_EMAIL_CAMPAIGN);
  const shown = (data ?? previousData)?.crmEmailCampaigns;

  const forget = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmEmailCampaigns' });
    client.cache.gc();
  };

  const copy = async (c: Campaign) => {
    try {
      const { data: made } = await duplicate({ variables: { id: c.id } });
      forget();
      toast.success(`Made ${made.crmDuplicateEmailCampaign.name}, a draft.`);
      navigate(emailCampaignPath(made.crmDuplicateEmailCampaign.id));
    } catch (e) {
      toast.error(describeEmailError(e, 'Could not duplicate it.'));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await remove({ variables: { id: deleting.id } });
      forget();
      toast.success(`Deleted ${deleting.name}.`);
      setDeleting(null);
    } catch {
      // Shown in the dialog.
    }
  };

  const newButton = (
    <Button component={RouterLink} to={newEmailCampaignPath} variant="contained" startIcon={<AddIcon />}>
      New campaign
    </Button>
  );

  return (
    <CrmPageFrame
      title="Email"
      description="Campaigns sent from the dashboard, and the 4,633 migrated from NationBuilder with their figures."
      actions={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<SendOutlinedIcon />} onClick={() => setTesting(true)}>
            Send test email
          </Button>
          {newButton}
        </Box>
      }
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <StatTiles label="Show" columns={4}>
          {TILES.map((t) => (
            <StatTile
              key={t.view}
              label={t.label}
              hint={t.hint}
              icon={t.icon}
              tone={t.tone}
              value={totals.data?.[t.view]?.total}
              selected={view === t.view}
              onClick={() => go({ view: view === t.view ? 'all' : t.view, page: 0 })}
            />
          ))}
        </StatTiles>

        <Box sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TextField
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search by name or subject"
              fullWidth
              inputProps={{ 'aria-label': 'Search campaigns', maxLength: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: typed ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={() => { setTyped(''); go({ search: '', page: 0 }); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {shown
                ? `${count(shown.total)} ${shown.total === 1 ? 'campaign' : 'campaigns'}${view === 'all' ? ' of ours' : `: ${TILES.find((t) => t.view === view)?.label.toLowerCase()}`}${search ? ` matching "${search}"` : ''}, newest first`
                : 'Loading…'}
            </Typography>
          </Box>
          {loading && shown ? <LinearProgress sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={() => refetch()}>Try again</Button>}>
              {describeEmailError(error, 'Could not load the campaigns.')}
            </Alert>
          )}

          {data && data.crmEmailCampaigns.total === 0 && (
            <EmptyState
              icon={<MailOutlineIcon />}
              title={search ? 'No campaigns match' : view === 'all' ? 'No campaigns yet' : 'None here'}
              text={search ? `Nothing matches "${search}".` : view === 'all' ? 'Write one to a list or saved filter. NationBuilder’s are under “From NationBuilder”, and any can be duplicated.' : 'Pick another tile, or show everything.'}
              action={view === 'all' && !search ? newButton : <Button variant="outlined" onClick={() => { setTyped(''); go({ view: 'all', search: '', page: 0 }); }}>Show everything</Button>}
            />
          )}

          {(!shown || shown.items.length > 0) && (
            <TableContainer sx={{ ...TABLE_SX, opacity: loading && shown ? 0.6 : 1 }}>
              <Table size="small" aria-label="Campaigns">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Opened</TableCell>
                    <TableCell align="right">Clicked</TableCell>
                    <TableCell align="right">Unsubscribed</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={visuallyHidden}>
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!shown && <LoadingRows columns={9} />}
                  {shown?.items.map((c) => {
                    const status = STATUS_META[c.status];
                    const when = whenOf(c);
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ minWidth: 240, maxWidth: 420 }}>
                          <Link component={RouterLink} to={emailCampaignPath(c.id)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>
                            {c.name}
                          </Link>
                          <Typography variant="caption" color="text.secondary" component="p" noWrap title={c.subject ?? undefined}>
                            {c.subject || 'No subject yet'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip size="small" label={status.label} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, color: status.tone, bgcolor: alpha(status.tone, 0.12) }} />
                          {c.workflowStepCount > 0 && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              Workflow email
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.segmentName ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{when.label}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{c.sentCount ? count(c.sentCount) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          <RateCell rate={c.openRate} n={c.openCount} tone="#059669" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          <RateCell rate={c.clickRate} n={c.clickCount} tone="#0891b2" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{c.sentCount ? count(c.unsubCount) : '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Duplicate into a new draft">
                            <IconButton size="small" aria-label={`Duplicate ${c.name}`} onClick={() => copy(c)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isDeletable(c) && !isMigrated(c) && (
                            <Tooltip title="Delete">
                              <IconButton size="small" aria-label={`Delete ${c.name}`} onClick={() => setDeleting(c)} sx={{ '&:hover': { color: 'error.main' } }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {shown && shown.total > PAGE && (
            <>
              <Divider />
              <TablePagination component="div" count={shown.total} page={page} onPageChange={(_, p) => go({ page: p })} rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} />
            </>
          )}
        </Box>
      </Box>

      {testing && <TestEmailDialog onClose={() => setTesting(false)} />}

      {deleting && (
        <DeleteDialog
          title={`Delete ${deleting.name}?`}
          confirm="Delete"
          running={removal.loading}
          error={removal.error ? describeEmailError(removal.error) : undefined}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        >
          <span>It never sent to anyone, so no figures are lost.</span>
        </DeleteDialog>
      )}
    </CrmPageFrame>
  );
};

/** A rate with a small bar under it, so a column of them reads at a glance. */
const RateCell = ({ rate, n, tone }: { rate: number | null; n: number; tone: string }) =>
  rate == null ? (
    <>—</>
  ) : (
    <Tooltip title={`${count(n)} people`}>
      <Box sx={{ display: 'inline-block', minWidth: 48 }}>
        {formatRate(rate)}
        <Box aria-hidden sx={{ height: 3, mt: 0.25, borderRadius: 2, bgcolor: '#eef1f5', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${Math.min(100, rate * 100)}%`, bgcolor: tone }} />
        </Box>
      </Box>
    </Tooltip>
  );
